<template>
  <etapa-formulario>
    <secao-formulario
      :icone="false"
      campos-obrigatorios
      rotulo="Veículo de reserva"
    />
    <div
      class="etapa-formulario__campo etapa-formulario__campo--md-6 etapa-formulario__campo--xl-6"
    >
      <campo-selecao
        obrigatorio
        :value="veiculoReservaSelecionado"
        :opcoes="opcoesVeiculoReserva"
        :erro="validacao.veiculoReserva"
        :desabilitado="!campoHabilitado('veiculoReserva')"
        :placeholder="'Selecione se precisa de veículo reserva ou não'"
        @input="atualizarCampo('veiculoReserva', $event)"
        rotulo="Você precisa de um veículo reserva para continuar seu projeto?"
      />
    </div>

    <transition name="fade">
      <div
        v-if="mostrarInformacoesAdicionais"
        class="etapa-formulario__campo etapa-formulario__campo--md-12 etapa-formulario__campo--xl-12"
      >
        <div class="my-3">
          <secao-formulario
            :icone="false"
            campos-obrigatorios
            rotulo="Informações adicionais"
          />
        </div>
        <campo-textarea
          :linhas="4"
          obrigatorio
          :value="value.informacoesAdicionais"
          :erro="validacao.informacoesAdicionais"
          :desabilitado="!campoHabilitado('informacoesAdicionais')"
          :maxlength="4000"
          :minimo="50"
          exibir-contador
          :exibir-aviso-limite="false"
          placeholder="Informe suas observações"
          @input="atualizarCampo('informacoesAdicionais', $event)"
          rotulo="Gostaria de relatar ou incluir alguma informação que não foi possível preencher nos campos anteriores?"
        />
      </div>
    </transition>

    <secao-formulario
      v-if="!modoVisualizacao"
      :icone="false"
      rotulo="Pendências para finalizar"
    />
    <pendencias-finalizacao
      v-if="!modoVisualizacao"
      :pendencias="pendenciasFinalizacao"
    />
  </etapa-formulario>
</template>

<script lang="js">
import CampoSelecao from "../campos/CampoSelecao.vue";
import EtapaFormulario from "../EtapaFormulario.vue";
import SecaoFormulario from "../SecaoFormulario.vue";
import CampoTextarea from "../campos/CampoTextarea.vue";
import PendenciasFinalizacao from "../PendenciasFinalizacao.vue";
import { validarValidacaoOcorrencia, validarCampoValidacaoOcorrencia } from "../../../domain/validators/validacaoOcorrencia.validator.js";

/**
 * Etapa "Validação da Ocorrência" do cadastro/visualização de relato.
 *
 * Coleta em cadastro: `veiculoReserva` (campo-selecao `S`/`N` via `opcoesValidacaoOcorrencia.veiculoReserva`)
 * e `informacoesAdicionais` (textarea quando `S`).
 * A Declaração de Veracidade acontece no modal de Finalizar (container),
 * não mais via checkbox nesta etapa.
 * Em visualização (`inject modoVisualizacao=true` via `VisualizarRelatoOcorrenciaContainer`):
 * - `value.veiculoReserva` vem normalizado de `GET /ocorrencia/{id}/validacao-ocorrencia` (`"Sim"`→`"S"` via `visualizarValidacaoOcorrenciaAdapter`).
 * - `opcoesValidacaoOcorrencia` é fornecido pelo container (fallback `OPCOES_SIM_NAO` ou `GET /validacao-ocorrencia-opcoes`).
 * - `CampoSelecao`/`CampoTextarea` ficam desabilitados via `inject`; `validar()` bypassa em visualização.
 *
 * @prop {Object} value - seção `ocorrencia.validacao` – `{ veiculoReserva: "S"|"N"|{value,label}|null, informacoesAdicionais: string }`
 * @prop {import("../../../types/index").OpcoesValidacaoOcorrencia} opcoesValidacaoOcorrencia - opções `veiculoReserva` vindas de `opcoesValidacaoOcorrenciaAdapter` (ou fallback `S`/`N`)
 * @prop {Array} [opcoesSelecione] - legado
 * @prop {Object} [opcoes] - legado
 *
 * @inject {boolean} modoVisualizacao - desabilita inputs e validação
 *
 * @emits input {Object} seção atualizada (`{ ...value, [campo]: valor }`)
 * @emits salvar {Object} `value` quando validado (ignorado em visualização)
 * @emits proxima - avança de aba quando validado
 */
export default {
  name: "ValidacaoOcorrencia",

  inject: {
    modoVisualizacao: {
      type: Boolean,
      default: false
    }
  },

  components: {
    CampoSelecao,
    CampoTextarea,
    EtapaFormulario,
    SecaoFormulario,
    PendenciasFinalizacao
  },

  props: {
    value: {
      type: Object,
      required: true
    },

    opcoesSelecione: {
      type: Array,
      default: () => []
    },

    opcoes: {
      type: Object,
      default: () => ({})
    },

    opcoesValidacaoOcorrencia: {
      type: Object,
      default: () => ({})
    },

    pendenciasFinalizacao: {
      type: Array,
      default: () => []
    }
  },

  data() {
    return {
      /** @type {Object<string, string>} mensagens de erro por campo. */
      validacao: {}
    };
  },

  computed: {
    opcoesVeiculoReserva() {
      return this.opcoesValidacaoOcorrencia?.veiculoReserva || [];
    },

    veiculoReservaSelecionado() {
      const valor = this.extrairValor(this.value.veiculoReserva);
      if (valor === null || valor === undefined || valor === "") return null;
      return (
        this.opcoesVeiculoReserva.find(
          opcao => String(opcao.value) === String(valor)
        ) || { value: valor, label: String(valor) }
      );
    },

    mostrarInformacoesAdicionais() {
      return this.extrairValor(this.value.veiculoReserva) === "S";
    },
    sequenciaValidacao() {
      return ["veiculoReserva", "informacoesAdicionais"];
    }
  },

  methods: {
    extrairValor(valor) {
      if (valor == null) return valor;
      if (typeof valor === "object") return valor.value ?? null;
      return valor;
    },
    temValorPreenchido(val) {
      if (val === null || val === undefined || val === "") return false;
      if (typeof val === "object") {
        const inner = val.value !== undefined ? val.value : val.label;
        return inner !== null && inner !== undefined && inner !== "";
      }
      return String(val).trim() !== "";
    },
    /**
     * Habilitação progressiva: o primeiro campo sempre libera;
     * os demais liberam quando todos os anteriores estão preenchidos.
     */
    campoHabilitado(campo) {
      if (this.modoVisualizacao) return false;
      const ordem = this.sequenciaValidacao;
      const indice = ordem.indexOf(campo);
      if (indice <= 0) return true;
      for (let i = 0; i < indice; i += 1) {
        if (!this.temValorPreenchido(this.value[ordem[i]])) return false;
      }
      return true;
    },
    atualizarCampo(campo, valor) {
      const valorNormalizado = this.extrairValor(valor);
      const atualizado = { ...this.value, [campo]: valorNormalizado };

      if (campo === "veiculoReserva" && valorNormalizado !== "S") {
        atualizado.informacoesAdicionais = "";
        this.$delete(this.validacao, "informacoesAdicionais");
      }

      this.$emit("input", atualizado);

      const erro = validarCampoValidacaoOcorrencia(campo, valorNormalizado, atualizado);
      if (erro) {
        if (this.validacao[campo] || String(valorNormalizado ?? "").trim().length > 0) {
          this.$set(this.validacao, campo, erro);
        }
      } else {
        this.$delete(this.validacao, campo);
      }

      if (this.extrairValor(atualizado.veiculoReserva) !== "S") {
        this.$delete(this.validacao, "informacoesAdicionais");
      }
    },

    salvar() {
      if (this.validar()) {
        this.$emit("salvar", this.value);
      }
    },

    proximaAba() {
      if (this.validar()) {
        this.$emit("proxima");
      }
    },

    validar() {
      if (this.modoVisualizacao) return true;
      const { isValid, erros } = validarValidacaoOcorrencia(this.value);
      this.validacao = erros;
      return isValid;
    }
  }
};
</script>

<style lang="scss" scoped>
@import "../../../styles/_main";
@import "../../../styles/layout/_transitions";
</style>



